import type { CustomerProfileView } from '../models/customer-profile.types';

const EM = '—';

function pickStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string') {
      const t = v.trim();
      if (t.length > 0) return t;
    }
    if (typeof v === 'number' && Number.isFinite(v)) {
      return String(v);
    }
  }
  return '';
}

function unwrapRecord(body: unknown): Record<string, unknown> | null {
  if (body == null || typeof body !== 'object') {
    return null;
  }
  const b = body as Record<string, unknown>;
  const data = b['data'];
  if (data != null && typeof data === 'object') {
    return data as Record<string, unknown>;
  }
  const customer = b['customer'];
  if (customer != null && typeof customer === 'object') {
    return customer as Record<string, unknown>;
  }
  const user = b['user'];
  if (user != null && typeof user === 'object') {
    return user as Record<string, unknown>;
  }
  return b;
}

function formatMemberSince(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  return d
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .replace(',', '');
}

function pickMemberSince(root: Record<string, unknown>): string {
  const keys = ['member_since', 'created_at', 'registered_at', 'joined_at', 'createdAt'];
  for (const k of keys) {
    const v = root[k];
    if (typeof v === 'string' && v.trim()) {
      return formatMemberSince(v.trim());
    }
  }
  return EM;
}

/** Maps to `customers.id` (typically `id` on the row; some APIs expose `customer_id`). */
function pickCustomersId(root: Record<string, unknown>): number | null {
  const keys = ['id', 'customer_id', 'customerId'];
  for (const k of keys) {
    const v = root[k];
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v;
    }
    if (typeof v === 'string' && /^\d+$/.test(v.trim())) {
      const n = Number(v.trim());
      if (Number.isSafeInteger(n)) {
        return n;
      }
    }
  }
  return null;
}

/**
 * Maps typical Nest / JSON shapes (`data`, `customer`, snake_case fields) into a view model.
 */
export function normalizeCustomerProfileBody(body: unknown): Partial<CustomerProfileView> {
  const root = unwrapRecord(body);
  if (!root) {
    return {};
  }

  return {
    customerId: pickCustomersId(root),
    memberId: pickStr(root, ['member_id', 'memberId']),
    name: pickStr(root, ['name', 'full_name', 'fullName']),
    imgUrl: (() => {
      const u = pickStr(root, ['img_url', 'avatarUrl', 'avatar_url', 'photo_url', 'imageUrl']);
      return u.length > 0 ? u : null;
    })(),
    referralCode: pickStr(root, ['referral_code', 'referralCode', 'referral']),
    mobile: pickStr(root, ['mobile', 'phone', 'phone_number', 'mobileNumber']),
    email: pickStr(root, ['email', 'email_address']),
    province: pickStr(root, ['province', 'region', 'state']),
    city: pickStr(root, ['city', 'town', 'town_city']),
    memberSinceLabel: pickMemberSince(root),
  };
}

export function mergeCustomerProfile(
  partial: Partial<CustomerProfileView>,
  fallbackEmail: string | null,
  fallbackAvatarUrl: string | null,
): CustomerProfileView {
  const email = partial.email?.trim() || fallbackEmail?.trim() || '';
  const img =
    partial.imgUrl && partial.imgUrl.trim().length > 0
      ? partial.imgUrl.trim()
      : fallbackAvatarUrl?.trim() || null;

  return {
    customerId:
      partial.customerId != null && Number.isFinite(partial.customerId) ? partial.customerId : null,
    memberId: partial.memberId?.trim() || EM,
    name: partial.name?.trim() || EM,
    imgUrl: img,
    referralCode: partial.referralCode?.trim() || '',
    mobile: partial.mobile?.trim() || EM,
    email: email || EM,
    province: partial.province?.trim() || EM,
    city: partial.city?.trim() || EM,
    memberSinceLabel: partial.memberSinceLabel?.trim() || EM,
  };
}

/** Restore `CustomerProfileView` from localStorage JSON. */
export function parseCustomerProfileFromJson(raw: unknown): CustomerProfileView | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const partial: Partial<CustomerProfileView> = {
    customerId:
      typeof o['customerId'] === 'number' && Number.isFinite(o['customerId'])
        ? o['customerId']
        : undefined,
    memberId: typeof o['memberId'] === 'string' ? o['memberId'] : undefined,
    name: typeof o['name'] === 'string' ? o['name'] : undefined,
    imgUrl:
      o['imgUrl'] === null
        ? null
        : typeof o['imgUrl'] === 'string'
          ? o['imgUrl']
          : undefined,
    referralCode: typeof o['referralCode'] === 'string' ? o['referralCode'] : undefined,
    mobile: typeof o['mobile'] === 'string' ? o['mobile'] : undefined,
    email: typeof o['email'] === 'string' ? o['email'] : undefined,
    province: typeof o['province'] === 'string' ? o['province'] : undefined,
    city: typeof o['city'] === 'string' ? o['city'] : undefined,
    memberSinceLabel:
      typeof o['memberSinceLabel'] === 'string' ? o['memberSinceLabel'] : undefined,
  };
  return mergeCustomerProfile(partial, null, null);
}
